import { CKEditor } from '@ckeditor/ckeditor5-react';
import {
    ClassicEditor,
    Bold,
    Essentials,
    Heading,
    Indent,
    IndentBlock,
    Italic,
    Link,
    List,
    MediaEmbed,
    Paragraph,
    Table,
    Undo,
    HtmlComment,
    HtmlEmbed,
    HtmlEmbedCommand,
    Alignment,
    Image,
    ImageUpload,
    ImageInsert,
    ImageResize,
    ImageStyle,
    ImageEditing,
    ImageBlockEditing,
    ImageCaption,
    ImageToolbar,
    Font,
    FontColor,
    SourceEditing,
    GeneralHtmlSupport,
    Fullscreen,
    SimpleUploadAdapter,
} from 'ckeditor5';

import 'ckeditor5/ckeditor5.css';
import './ck-editor.css';
import { store } from '../../redux/store';
import endpoints from '../../app/services/endpoints';

// Custom Upload Adapter for CKEditor
class CustomUploadAdapter {
    loader: any;
    xhr: XMLHttpRequest | null;

    constructor(loader: any) {
        this.loader = loader;
        this.xhr = null;
    }

    upload() {
        return this.loader.file.then((file: File) => new Promise((resolve, reject) => {
            this._initRequest();
            this._initListeners(resolve, reject, file);
            this._sendRequest(file);
        }));
    }

    abort() {
        if (this.xhr) {
            this.xhr.abort();
        }
    }

    _initRequest() {
        const xhr = this.xhr = new XMLHttpRequest();
        const state = store.getState();
        const token = state.auth.token;

        xhr.open('POST', `${import.meta.env.VITE_API_DOMAIN}${endpoints.files.base}`, true);
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        xhr.responseType = 'json';
    }

    _initListeners(resolve: any, reject: any, file: File) {
        const xhr = this.xhr!;
        const loader = this.loader;
        const genericErrorText = `Couldn't upload file: ${file.name}.`;

        xhr.addEventListener('error', () => reject(genericErrorText));
        xhr.addEventListener('abort', () => reject());
        xhr.addEventListener('load', () => {
            const response = xhr.response;

            if (!response || response.error) {
                return reject(response && response.error ? response.error.message : genericErrorText);
            }

            // Construct the file URL from the response
            const fileName = response.data.fileName;
            const fileUrl = `${import.meta.env.VITE_API_DOMAIN}${endpoints.files.base}?fileName=${fileName}`;

            resolve({
                default: fileUrl
            });
        });

        if (xhr.upload) {
            xhr.upload.addEventListener('progress', (evt: ProgressEvent) => {
                if (evt.lengthComputable) {
                    loader.uploadTotal = evt.total;
                    loader.uploaded = evt.loaded;
                }
            });
        }
    }

    _sendRequest(file: File) {
        const data = new FormData();
        data.append('file', file);
        data.append('Caption', '');
        data.append('IsGlobalContent', 'true');

        this.xhr!.send(data);
    }
}

// Plugin to register the custom upload adapter
function CustomUploadAdapterPlugin(editor: any) {
    editor.plugins.get('FileRepository').createUploadAdapter = (loader: any) => {
        return new CustomUploadAdapter(loader);
    };
}

interface CKRichEditorProps {
    value?: string;
    onChange?: (data: string) => void;
    placeholder?: string;
    readOnly?: boolean;
    height?: string;
}

const CKRichEditor: React.FC<CKRichEditorProps> = ({
    value = '',
    onChange,
    placeholder = 'Enter your content here...',
    readOnly = false,
    height
}) => {
    return (
        <div className="ck-editor-container" style={{ minHeight: height, height: height, width: '100%' }}>
            <CKEditor
                editor={ClassicEditor}
                data={value}
                disabled={readOnly}
                onChange={(_, editor) => {
                    const data = editor.getData();
                    onChange?.(data);
                }}
                config={{
                    licenseKey: 'GPL',
                    placeholder,
                    toolbar: [
                        'undo', 'redo', '|',
                        'heading', '|', 'bold', 'italic', '|',
                        'fontColor', 'alignment',
                        'link', 'insertTable', 'mediaEmbed', '|',
                        'imageUpload', '|',
                        'bulletedList', 'numberedList', 'indent', 'outdent', '|',
                        'sourceEditing', 'htmlEmbed', '|',
                        'fullscreen'
                    ],
                    plugins: [
                        Bold, Essentials, Heading, Indent, IndentBlock,
                        Italic, Link, List, MediaEmbed, Paragraph,
                        Alignment,
                        Table, Undo, HtmlComment, HtmlEmbed,
                        HtmlEmbedCommand, SourceEditing, Image,
                        ImageInsert, ImageUpload, ImageResize, ImageStyle,
                        ImageEditing, ImageBlockEditing, ImageCaption, ImageToolbar,
                        Font, FontColor,
                        SimpleUploadAdapter,
                        GeneralHtmlSupport, Fullscreen
                    ],
                    extraPlugins: [CustomUploadAdapterPlugin],
                    heading: {
                        options: [
                            { model: 'paragraph', title: 'Paragraph', class: 'ck-heading_paragraph' },
                            { model: 'heading1', view: 'h1', title: 'Heading 1', class: 'ck-heading_heading1' },
                            { model: 'heading2', view: 'h2', title: 'Heading 2', class: 'ck-heading_heading2' },
                            { model: 'heading3', view: 'h3', title: 'Heading 3', class: 'ck-heading_heading3' }
                        ]
                    },
                    alignment: {
                        options: [ 'left', 'center', 'right', 'justify' ]
                    },
                    htmlSupport: {
                        allow: [
                            {
                                name: /.*/,
                                attributes: true,
                                classes: true,
                                styles: true,
                            }
                        ],
                        disallow: []
                    },
                    sourceEditing: {
                        allowCollaborationFeatures: true,
                    },
                    htmlEmbed: {
                        showPreviews: true,
                        sanitizeHtml: (inputHtml) => {
                            return {
                                html: inputHtml,
                                hasChanged: false
                            };
                        }
                    },
                    image: {
                        toolbar: [
                            'imageStyle:inline',
                            'imageStyle:wrapText',
                            'imageStyle:breakText',
                            '|',
                            'toggleImageCaption',
                            'imageTextAlternative',
                            '|',
                            'imageResize'
                        ],
                        resizeOptions: [
                            {
                                name: 'resizeImage:original',
                                label: 'Original',
                                value: null
                            },
                            {
                                name: 'resizeImage:50',
                                label: '50%',
                                value: '50'
                            },
                            {
                                name: 'resizeImage:75',
                                label: '75%',
                                value: '75'
                            }
                        ]
                    },
                }}
            />
        </div>
    );
}

export default CKRichEditor;
